import React, { useState, useEffect } from 'react';
import { Zap, ChevronRight, ChevronLeft, MessageSquare, CheckSquare, Clock, Shield, AlertCircle, Tag } from 'lucide-react';
import { useAutomation } from '../context/AutomationContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import PlaybookEngine from './PlaybookEngine';

/**
 * PlaybookModal - Step-based wizard for creating/editing playbooks
 *
 * Routes:
 *   New-style trigger events (quote_*, job_*, invoice_*) → backend API → companies/{cId}/playbooks
 *   Legacy trigger events (leadCreated, stageChanged) → Firestore direct → companies/{cId}/automations
 */

// Trigger types that are executed server-side by the backend engine
const BACKEND_TRIGGER_EVENTS = [
  'quote_created',
  'quote_sent',
  'quote_approved',
  'job_scheduled',
  'job_completed',
  'invoice_sent',
  'invoice_paid',
];

const TRIGGER_LABELS = {
  quote_created: 'When a quote is created',
  quote_sent: 'When a quote is sent',
  quote_approved: 'When a quote is approved',
  job_scheduled: 'When a job is scheduled',
  job_completed: 'When a job is completed',
  invoice_sent: 'When an invoice is sent',
  invoice_paid: 'When an invoice is paid',
  leadCreated: 'When a lead is created',
  stageChanged: 'When pipeline stage changes',
};

const STEPS = [
  { id: 'trigger', label: 'Trigger', icon: Zap },
  { id: 'delay', label: 'Delay', icon: Clock },
  { id: 'action', label: 'Action', icon: CheckSquare },
  { id: 'safeguards', label: 'Safeguards', icon: Shield },
];

const MERGE_TAGS_LEGACY = [
  { tag: '{{first_name}}', label: 'First Name' },
  { tag: '{{last_name}}', label: 'Last Name' },
  { tag: '{{name}}', label: 'Full Name' },
  { tag: '{{company}}', label: 'Company' },
  { tag: '{{city}}', label: 'City' },
  { tag: '{{state}}', label: 'State' },
];

const MERGE_TAGS_BACKEND = [
  { tag: '{{customer_name}}', label: 'Customer Name' },
  { tag: '{{amount}}', label: 'Amount ($)' },
  { tag: '{{payment_link}}', label: 'Payment Link' },
  { tag: '{{company_name}}', label: 'Company Name' },
  { tag: '{{first_name}}', label: 'First Name' },
];

const initialFormState = {
  name: '',
  trigger: { type: 'quote_sent', from: '', to: '' },
  delay: { value: 0, unit: 'hours' },
  action: {
    type: 'sendSMS',
    sms: { message: '' },
    task: { title: '', description: '', priority: 'medium', taskType: 'call', dueOffsetDays: 0, dueOffsetTime: '09:00' },
    tag: { value: '' },
  },
  safeguards: { onlyIfNoReply: false, onlyOncePerLead: false, respectBusinessHours: false },
  active: true,
};

const PlaybookModal = ({ open, onClose, playbook, pipelineStages = [] }) => {
  const { addPlaybook, updatePlaybook, addAutomation, updateAutomation } = useAutomation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  const isEditing = !!playbook;

  // Initialize form when editing
  useEffect(() => {
    if (playbook) {
      // Detect backend playbook vs legacy automation
      if (playbook.triggerEvent) {
        // Backend playbook: map back to form format
        const firstAction = (playbook.actions || [])[0] || {};
        setForm({
          name: playbook.name || '',
          trigger: { type: playbook.triggerEvent, from: '', to: '' },
          delay: { value: 0, unit: 'hours' },
          action: {
            type: firstAction.type === 'send_sms' ? 'sendSMS'
              : firstAction.type === 'create_task' ? 'createTask'
              : firstAction.type === 'add_tag' ? 'addTag'
              : firstAction.type || 'sendSMS',
            sms: { message: firstAction.config?.message || '' },
            task: firstAction.config?.title ? { ...firstAction.config } : initialFormState.action.task,
            tag: { value: firstAction.config?.tag || '' },
          },
          safeguards: playbook.safeguards || { onlyIfNoReply: false, onlyOncePerLead: false, respectBusinessHours: false },
          active: playbook.isActive !== false,
        });
      } else {
        setForm({
          name: playbook.name || '',
          trigger: playbook.trigger || { type: 'quote_sent', from: '', to: '' },
          delay: playbook.delay || { value: 0, unit: 'hours' },
          action: playbook.action || initialFormState.action,
          safeguards: playbook.safeguards || { onlyIfNoReply: false, onlyOncePerLead: false, respectBusinessHours: false },
          active: playbook.active !== false,
        });
      }
    } else {
      setForm(initialFormState);
    }
    setStep(0);
  }, [playbook, open]);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a playbook name');
      return;
    }

    if (form.action.type === 'sendSMS' && !form.action.sms?.message?.trim()) {
      toast.error('Please enter an SMS message');
      setStep(2);
      return;
    }

    if (form.action.type === 'createTask' && !form.action.task?.title?.trim()) {
      toast.error('Please enter a task title');
      setStep(2);
      return;
    }

    if (form.action.type === 'addTag' && !form.action.tag?.value?.trim()) {
      toast.error('Please enter a tag value');
      setStep(2);
      return;
    }

    setSaving(true);

    const isBackendTrigger = BACKEND_TRIGGER_EVENTS.includes(form.trigger.type);

    try {
      if (isBackendTrigger) {
        // Build backend API payload
        const actionType = form.action.type === 'sendSMS' ? 'send_sms'
          : form.action.type === 'createTask' ? 'create_task'
          : form.action.type === 'addTag' ? 'add_tag'
          : form.action.type;

        const actionConfig = form.action.type === 'sendSMS'
          ? { message: form.action.sms?.message || '' }
          : form.action.type === 'addTag'
          ? { tag: form.action.tag?.value || '' }
          : { ...(form.action.task || {}) };

        const backendPayload = {
          name: form.name.trim(),
          triggerEvent: form.trigger.type,
          conditions: [],
          actions: [{ type: actionType, config: actionConfig }],
          safeguards: {
            onlyOncePerEntity: form.safeguards.onlyOncePerLead || false,
            cooldownHours: 24,
          },
          isActive: form.active,
        };

        if (isEditing && playbook?.triggerEvent) {
          await updatePlaybook(playbook.id, backendPayload);
          toast.success('Playbook updated');
        } else {
          await addPlaybook(backendPayload);
          toast.success('Playbook created — runs server-side on every matching event');
        }
      } else {
        // Legacy automation: direct Firestore write
        const payload = {
          name: form.name.trim(),
          trigger: form.trigger,
          delay: form.delay,
          action: form.action,
          safeguards: form.safeguards,
          active: form.active,
        };

        if (isEditing) {
          await updateAutomation(playbook.id, payload);
          toast.success('Playbook updated');
        } else {
          await addAutomation(payload);
          toast.success('Playbook created');
        }
      }

      onClose();
    } catch (error) {
      console.error('Failed to save playbook:', error);
      toast.error(error.message || 'Failed to save playbook');
    } finally {
      setSaving(false);
    }
  };

  const updateTrigger = (updates) => {
    setForm({ ...form, trigger: { ...form.trigger, ...updates } });
  };

  const updateDelay = (updates) => {
    setForm({ ...form, delay: { ...form.delay, ...updates } });
  };

  const updateAction = (updates) => {
    setForm({ ...form, action: { ...form.action, ...updates } });
  };

  const updateSms = (updates) => {
    setForm({
      ...form,
      action: {
        ...form.action,
        sms: { ...form.action.sms, ...updates },
      },
    });
  };

  const updateTask = (updates) => {
    setForm({
      ...form,
      action: {
        ...form.action,
        task: { ...form.action.task, ...updates },
      },
    });
  };

  const updateSafeguards = (updates) => {
    setForm({ ...form, safeguards: { ...form.safeguards, ...updates } });
  };

  const insertMergeTag = (tag) => {
    const currentMessage = form.action.sms?.message || '';
    updateSms({ message: currentMessage + tag });
  };

  // Preview interpolation
  const getMessagePreview = () => {
    const message = form.action.sms?.message || '';
    const isBackend = BACKEND_TRIGGER_EVENTS.includes(form.trigger.type);
    if (isBackend) {
      return message
        .replace(/\{\{customer_name\}\}/g, 'John Smith')
        .replace(/\{\{first_name\}\}/g, 'John')
        .replace(/\{\{amount\}\}/g, '450.00')
        .replace(/\{\{payment_link\}\}/g, 'https://pay.cleanbid.com/...')
        .replace(/\{\{company_name\}\}/g, 'Smith Cleaning');
    }
    return PlaybookEngine.interpolateMessage(message, {
      first_name: 'John',
      last_name: 'Smith',
      name: 'John Smith',
      company: 'Smith Roofing',
      city: 'New Haven',
      state: 'CT',
    });
  };

  const isBackendTrigger = BACKEND_TRIGGER_EVENTS.includes(form.trigger.type);
  const MERGE_TAGS = isBackendTrigger ? MERGE_TAGS_BACKEND : MERGE_TAGS_LEGACY;

  const renderStepContent = () => {
    switch (step) {
      case 0: // Trigger
        return (
          <div className="space-y-4">
            {isBackendTrigger && (
              <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>This trigger runs <strong>server-side</strong> — no active browser session needed.</span>
              </div>
            )}
            <div className="space-y-2">
              <Label>When should this playbook run?</Label>
              <Select
                value={form.trigger.type}
                onValueChange={(value) => updateTrigger({ type: value })}
                disabled={isEditing}
              >
                <SelectTrigger data-testid="trigger-type-select">
                  <SelectValue placeholder="Select trigger" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_h1" disabled className="text-gray-400 text-xs font-semibold cursor-default">
                    Server-side Events
                  </SelectItem>
                  {BACKEND_TRIGGER_EVENTS.map((ev) => (
                    <SelectItem key={ev} value={ev}>{TRIGGER_LABELS[ev]}</SelectItem>
                  ))}
                  <SelectItem value="_h2" disabled className="text-gray-400 text-xs font-semibold cursor-default mt-1">
                    Legacy (Client-side)
                  </SelectItem>
                  <SelectItem value="leadCreated">When a lead is created</SelectItem>
                  <SelectItem value="stageChanged">When pipeline stage changes</SelectItem>
                </SelectContent>
              </Select>
              {isEditing && (
                <p className="text-xs text-gray-500">Trigger type cannot be changed after creation.</p>
              )}
            </div>

            {form.trigger.type === 'stageChanged' && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">From Stage</Label>
                  <Select
                    value={form.trigger.from || '__any__'}
                    onValueChange={(value) => updateTrigger({ from: value === '__any__' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any stage</SelectItem>
                      {pipelineStages.map((stage) => (
                        <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">To Stage</Label>
                  <Select
                    value={form.trigger.to || '__any__'}
                    onValueChange={(value) => updateTrigger({ to: value === '__any__' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any stage</SelectItem>
                      {pipelineStages.map((stage) => (
                        <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        );

      case 1: // Delay
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>How soon after the trigger?</Label>
              <div className="grid grid-cols-2 gap-4">
                <Select
                  value={form.delay.value === 0 ? 'immediately' : 'delay'}
                  onValueChange={(value) => {
                    if (value === 'immediately') {
                      updateDelay({ value: 0 });
                    } else {
                      updateDelay({ value: form.delay.value || 1 });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediately">Immediately</SelectItem>
                    <SelectItem value="delay">After a delay</SelectItem>
                  </SelectContent>
                </Select>

                {form.delay.value > 0 && (
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={form.delay.value}
                      onChange={(e) => updateDelay({ value: parseInt(e.target.value) || 1 })}
                      className="w-20"
                    />
                    <Select
                      value={form.delay.unit}
                      onValueChange={(value) => updateDelay({ unit: value })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {form.delay.value > 0 && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>Delayed playbooks will execute after the specified time. In V1, this runs on the next app interaction.</p>
              </div>
            )}
          </div>
        );

      case 2: // Action
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>What should happen?</Label>
              <Select
                value={form.action.type}
                onValueChange={(value) => updateAction({ type: value })}
              >
                <SelectTrigger data-testid="action-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sendSMS">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Send SMS
                    </span>
                  </SelectItem>
                  <SelectItem value="createTask">
                    <span className="flex items-center gap-2">
                      <CheckSquare className="w-4 h-4" />
                      Create Task
                    </span>
                  </SelectItem>
                  {isBackendTrigger && (
                    <SelectItem value="addTag">
                      <span className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Add Tag to Lead
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Add Tag Action */}
            {form.action.type === 'addTag' && (
              <div className="space-y-2 pt-2">
                <Label>Tag to add</Label>
                <Input
                  value={form.action.tag?.value || ''}
                  onChange={(e) => setForm((f) => ({ ...f, action: { ...f.action, tag: { value: e.target.value } } }))}
                  placeholder="e.g. hot-lead, follow-up, vip"
                  data-testid="tag-value-input"
                />
                <p className="text-xs text-gray-500">This tag will be added to the lead associated with this event.</p>
              </div>
            )}

            {/* SMS Action Config */}
            {form.action.type === 'sendSMS' && (
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Message</Label>
                    <span className="text-xs text-gray-500">
                      {(form.action.sms?.message || '').length} characters
                    </span>
                  </div>
                  <Textarea
                    value={form.action.sms?.message || ''}
                    onChange={(e) => updateSms({ message: e.target.value })}
                    placeholder={isBackendTrigger
                      ? 'Hi {{customer_name}}, your quote for ${{amount}} is ready!'
                      : 'Hi {{first_name}}, thanks for reaching out...'}
                    rows={4}
                    data-testid="sms-message-input"
                  />
                </div>

                {/* Merge Tags */}
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Insert merge tag</Label>
                  <div className="flex flex-wrap gap-2">
                    {MERGE_TAGS.map(({ tag, label }) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => insertMergeTag(tag)}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                {form.action.sms?.message && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600">Preview</Label>
                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 border border-gray-200">
                      {getMessagePreview()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Task Action Config */}
            {form.action.type === 'createTask' && (
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label>Task Title</Label>
                  <Input
                    value={form.action.task?.title || ''}
                    onChange={(e) => updateTask({ title: e.target.value })}
                    placeholder="Follow up with lead"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Textarea
                    value={form.action.task?.description || ''}
                    onChange={(e) => updateTask({ description: e.target.value })}
                    placeholder="Additional details..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={form.action.task?.priority || 'medium'}
                      onValueChange={(value) => updateTask({ priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Task Type</Label>
                    <Select
                      value={form.action.task?.taskType || 'call'}
                      onValueChange={(value) => updateTask({ taskType: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="call">Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="follow-up">Follow-up</SelectItem>
                        <SelectItem value="visit">Visit</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Due in (days)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.action.task?.dueOffsetDays || 0}
                      onChange={(e) => updateTask({ dueOffsetDays: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Time</Label>
                    <Input
                      type="time"
                      value={form.action.task?.dueOffsetTime || '09:00'}
                      onChange={(e) => updateTask({ dueOffsetTime: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 3: // Safeguards
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Optional safeguards to control when this playbook runs.
            </p>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Only if no reply yet</p>
                  <p className="text-sm text-gray-500">Skip if the lead has already replied</p>
                </div>
                <Switch
                  checked={form.safeguards?.onlyIfNoReply || false}
                  onCheckedChange={(checked) => updateSafeguards({ onlyIfNoReply: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Only once per lead</p>
                  <p className="text-sm text-gray-500">Do not run this playbook twice for the same lead</p>
                </div>
                <Switch
                  checked={form.safeguards?.onlyOncePerLead || false}
                  onCheckedChange={(checked) => updateSafeguards({ onlyOncePerLead: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Respect business hours</p>
                  <p className="text-sm text-gray-500">Only send during 8am-6pm local time</p>
                </div>
                <Switch
                  checked={form.safeguards?.respectBusinessHours || false}
                  onCheckedChange={(checked) => updateSafeguards({ respectBusinessHours: checked })}
                />
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>Safeguards are checked before each playbook execution. In V1, some safeguards run on best-effort basis.</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Playbook' : 'Create Playbook'}
          </DialogTitle>
        </DialogHeader>

        {/* Playbook Name */}
        <div className="space-y-2 pb-4 border-b border-gray-200">
          <Label htmlFor="playbook-name">Playbook Name</Label>
          <Input
            id="playbook-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., New Lead → Welcome SMS"
            data-testid="playbook-name-input"
          />
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-between py-4">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isActive = idx === step;
            const isComplete = idx < step;

            return (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  onClick={() => setStep(idx)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                    ${isActive ? 'bg-[rgba(242,107,29,0.1)] text-[var(--cb-orange)]' : ''}
                    ${isComplete ? 'text-green-600' : ''}
                    ${!isActive && !isComplete ? 'text-gray-400' : ''}
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
                </button>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="min-h-[280px]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div>
            {step > 0 && (
              <Button type="button" variant="ghost" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>

            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="text-white"
                style={{ backgroundColor: 'var(--cb-orange)' }}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="text-white"
                style={{ backgroundColor: 'var(--cb-orange)' }}
                data-testid="save-playbook-btn"
              >
                {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Playbook'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlaybookModal;
